import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Sparkles, LayoutGrid, FileBarChart, Building2, MessageSquare, Brain, Bell, Settings, Search, History, Star } from "lucide-react";
import api from "@/lib/api";
import { modulesForIds, getRecentModules, recordRecentModule } from "@/lib/modules";

const NAV = [
  { label: "Command Center", to: "/", icon: LayoutGrid },
  { label: "Reports & Analytics", to: "/reports", icon: FileBarChart },
  { label: "Programs & Facilities", to: "/programs", icon: Building2 },
  { label: "Messages", to: "/messages", icon: MessageSquare },
  { label: "AI Insights", to: "/ai-insights", icon: Brain },
  { label: "Alerts", to: "/alerts", icon: Bell },
  { label: "Administration & Settings", to: "/settings", icon: Settings },
];

export function CommandPalette({ open, onOpenChange, onOpenCopilot }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [allowedIds, setAllowedIds] = useState(null);
  const [recents, setRecents] = useState([]);
  const [pinned, setPinned] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/access/me").then((r) => setAllowedIds(r.data.module_ids)).catch(() => {});
      api.get("/preferences").then((r) => setPinned(r.data.pinned || [])).catch(() => {});
      setRecents(getRecentModules());
    }
    if (!open) setQ("");
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.results || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (to) => { onOpenChange(false); navigate(to); };
  const launchModule = (m) => { recordRecentModule(m.n); go(m.to); };
  const lc = q.trim().toLowerCase();
  const allowed = modulesForIds(allowedIds);
  const nav = NAV.filter((n) => !lc || n.label.toLowerCase().includes(lc));
  const modules = allowed.filter((m) => !lc || m.title.toLowerCase().includes(lc));
  const recentModules = recents
    .map((n) => allowed.find((m) => m.n === n))
    .filter(Boolean)
    .filter((m) => !lc || m.title.toLowerCase().includes(lc));
  const favoriteModules = pinned
    .map((n) => allowed.find((m) => m.n === n))
    .filter(Boolean)
    .filter((m) => !lc || m.title.toLowerCase().includes(lc));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput data-testid="command-input" value={q} onValueChange={setQ}
        placeholder="Search modules, pages, residents, staff… or ask the Copilot" />
      <CommandList>
        <CommandEmpty>No matches. Try asking the AI Copilot above.</CommandEmpty>

        <CommandGroup heading="AI">
          <CommandItem value="ask-copilot" onSelect={() => { onOpenChange(false); onOpenCopilot(); }} data-testid="cmd-ask-copilot">
            <Sparkles className="mr-2 h-4 w-4 text-[#7C3AED]" />
            Ask CAREIQ Copilot{lc ? `: “${q}”` : ""}
          </CommandItem>
        </CommandGroup>

        {favoriteModules.length > 0 && (
          <CommandGroup heading="Favorites">
            {favoriteModules.map((m) => (
              <CommandItem key={`fav-${m.n}`} value={`fav-${m.n}`} onSelect={() => launchModule(m)} data-testid={`cmd-fav-${m.n}`}>
                <Star className="mr-2 h-4 w-4 fill-[#F79009] text-[#F79009]" /> {m.title.replace("CAREIQ ", "")}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {recentModules.length > 0 && (
          <CommandGroup heading="Recent">
            {recentModules.map((m) => (
              <CommandItem key={`recent-${m.n}`} value={`recent-${m.n}`} onSelect={() => launchModule(m)} data-testid={`cmd-recent-${m.n}`}>
                <History className="mr-2 h-4 w-4 text-muted-foreground" /> {m.title.replace("CAREIQ ", "")}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.length > 0 && (
          <CommandGroup heading="Search results">
            {results.map((r, i) => (
              <CommandItem key={i} value={`res-${i}`} onSelect={() => go(r.link)} data-testid={`cmd-result-${i}`}>
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{r.title}</span>
                {r.subtitle && <span className="ml-2 text-xs text-muted-foreground">{r.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {nav.length > 0 && (
          <CommandGroup heading="Navigate">
            {nav.map((n) => (
              <CommandItem key={n.to} value={`nav-${n.to}`} onSelect={() => go(n.to)} data-testid={`cmd-nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}>
                <n.icon className="mr-2 h-4 w-4" /> {n.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {modules.length > 0 && (
          <CommandGroup heading="Launch module">
            {modules.map((m) => (
              <CommandItem key={m.n} value={`mod-${m.n}`} onSelect={() => launchModule(m)} data-testid={`cmd-module-${m.n}`}>
                <m.icon className="mr-2 h-4 w-4" style={{ color: m.color }} /> {m.title.replace("CAREIQ ", "")}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
